import { PatientOrder } from './patient/order';
import { Practice } from './practice';
import { User, UserIdentification } from './user';

interface EhrAppointment {
  assistantId: string;
  appointmentEhrId: string;
  patientEhrId: string;
  practiceId: string;
  providerId?: string;
}

interface EncounterAppointment {
  existingEncounterId: string;
  authenticationCode: string;
}

export type AppointmentReschedulingBookingIdentifier =
  | { ehrTimeSlotBookingId: string }
  | EncounterAppointment
  | EhrAppointment;

export type AppointmentReschedulingBookingParams =
  AppointmentReschedulingBookingIdentifier & {
    newSlotId: string;
    appointmentKind: string;
    startTime: Date;
    endTime: Date;
  };

export type RescheduleIdentifier =
  | { ehrTimeSlotBookingId: string }
  | Pick<EncounterAppointment, 'existingEncounterId'>;

export type AppointmentReschedulingFetchingIdentifier =
  | RescheduleIdentifier
  | EhrAppointment;

export type AppointmentReschedulingFetchingParams =
  AppointmentReschedulingFetchingIdentifier & {
    startTime?: Date;
    endTime?: Date;
  };

export interface PracticeTimeSlot
  extends Pick<
    Practice,
    | 'name'
    | 'address'
    | 'city'
    | 'state'
    | 'zip'
    | 'timezone'
    | 'longitude'
    | 'latitude'
  > {
  practicePhoneNumber: string;
  distance?: number;
}

export interface EhrTimeSlotPatientOrder
  extends Pick<
    PatientOrder,
    | 'ehrCreated'
    | 'ehrScheduled'
    | 'followUpValue'
    | 'followUpUnit'
    | 'seriesAppointmentCount'
  > {
  referredToProvider?: User;
  alternateReferredToProvider?: User;
}

export interface AvailableEhrTimeSlotResponse {
  availableEhrTimeSlots: EhrTimeSlot[];
  practices: Record<string, PracticeTimeSlot>;
  providers: Record<string, UserIdentification>;
  patientOrder?: EhrTimeSlotPatientOrder;
  orders?: Record<string, PatientOrder>;
  showConfirmation?: boolean;
  mainProviderUserId?: string;
}

export enum Sorting {
  AlphabeticallyPractice = 'ALPHABETICALLY_PRACTICE',
  AlphabeticallyProvider = 'ALPHABETICALLY_PROVIDER',
  AvailabilityPractice = 'AVAILABILITY_PRACTICE',
  AvailabilityProvider = 'AVAILABILITY_PROVIDER',
  Distance = 'DISTANCE',
}

export interface EhrTimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
  appointmentKind: AppointmentKind;
  practiceId: string;
  providerId?: string;
  comment?: string;
  distance?: number;
}

export interface TimeSlotConfirmation {
  providerFirstName: string | null;
  providerLastName: string | null;
  providerPrefix: string | null;
  providerSuffix: string | null;
  previsitImageUrl: string | null;
  practiceName: string | null;
  timezone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  startTime: Date;
  ehrTimeSlotBookingId: string;
  cancellationSuccess: boolean | null;
  rescheduleAppointmentKind?: AppointmentKind;
}

export type AppointmentQuery = {
  startTime?: Date;
  endTime?: Date;
  previsitFormTemplateFieldConfigId: string;
  providerIds?: string[];
  practiceIds?: string[];
};

export type AppointmentKind = string;

export enum AppointmentStatus {
  Arrived = 'ARRIVED',
  Confirmed = 'CONFIRMED',
  CancellationIntended = 'CANCELLATION_INTENDED',
  Cancelled = 'CANCELLED',
  CheckedIn = 'CHECKED_IN',
  CheckedOut = 'CHECKED_OUT',
  Rescheduled = 'RESCHEDULED',
}
